import React, { useState, useEffect, useRef, useCallback } from "react";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import ReusableList from "../../components/ReusableList";
import Loader from "../components/Loader";

import ExportSearchResultsCSV from "./components/ExportSearchResultsCSV";

import { getAllCochargeUsers, getCochargeUsersByEmail } from "../../services/owners";
import { mapAppUserToAppUserDisplayObject } from "./helpers";
import AppUserLookupModal from "./components/AppUserLookupModal";

const UserList = () => {
  const navigate = useNavigate();
  const toast = useRef(null);
  const [users, setUsers] = useState([]);

  const filteredUsersRef = useRef([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lookupEmail, setLookupEmail] = useState("");
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookedUpUsers, setLookedUpUsers] = useState([]);

  const columns = [
    { label: "Emaid", value: "emaid" },
    { label: "Name", value: "name" },
    { label: "Email", value: "email" },
    { label: "Permissions", value: "permissions" },
    { label: "Type", value: "connection" },
    { label: "ID", value: "id" },
  ];

  const prepareUsersForDisplay = useCallback(async () => {
    try {
      setIsLoading(true);
      const usersList = await getAllCochargeUsers();
      if (usersList.length === 0) {
        setUsers([]);
        return;
      }

      const appUsersDisplayFormat = usersList?.map((u) => {
        return mapAppUserToAppUserDisplayObject(u);
      });

      setUsers(appUsersDisplayFormat);
      filteredUsersRef.current = appUsersDisplayFormat;
      setIsLoading(false);
    } catch (e) {
      console.error("Failed to get user details for users:", e);
    }
  }, [setUsers]);

  useEffect(() => {
    prepareUsersForDisplay();
  }, [prepareUsersForDisplay, setIsLoading]);

  const handleRowClick = (e) => {
    navigate(`/users/${e?.data?.emaid}`);
  };

  const handleLookup = async () => {
    const trimmed = lookupEmail.trim();
    if (!trimmed) return;
    setIsLookingUp(true);
    const users = await getCochargeUsersByEmail(trimmed);
    setIsLookingUp(false);
    if (users.length > 0) {
      setLookedUpUsers(users);
    } else {
      toast.current.show({
        severity: "warn",
        summary: "User not found",
        detail: `No user found for "${trimmed}"`,
        life: 4000,
      });
    }
  };

  return (
    <div className="user-list-container">
      <Toast ref={toast} />
      <AppUserLookupModal
        users={lookedUpUsers}
        onHide={() => setLookedUpUsers([])}
      />
      <Card className="p-4" title="Users" subTitle="The list shows a limited number of users by recent activity. To find a user not visible here, use the look up by email on the right.">
        <div className="flex align-items-center justify-content-end gap-2 mb-4">
          <InputText
            value={lookupEmail}
            onChange={(e) => setLookupEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleLookup()}
            placeholder="Look up user by email"
            className="w-20rem"
          />
          <Button
            label="Look up"
            icon="pi pi-search"
            loading={isLookingUp}
            onClick={handleLookup}
            disabled={!lookupEmail.trim()}
          />
        </div>
        {isLoading ? (
          <Loader />
        ) : (
          <ReusableList
            data={users}
            loading={isLoading}
            columns={columns}
            searchable={true}
            onRowClick={handleRowClick}
            onValueChange={(value) => {
              filteredUsersRef.current = value;
            }}
            actionButtons={
              <ExportSearchResultsCSV dataRef={filteredUsersRef} />
            }
          />
        )}
      </Card>
    </div>
  );
};

export default UserList;
